


































import { SportOfferDataStore } from '@store/modules/data/sportOffer';
import {
    SdsCarousel,
    SdsSectionTitle,
} from '@superbet-group/web.lib.ui-components';
import { isAfterDate } from '@superbet-group/web.lib.utilities';
import { useVuexStore } from '@superbet-group/web.lib.vue-utilities';
import {
    computed,
    defineComponent,
    PropType,
    watch,
} from '@vue/composition-api';
import PopularSuperbet from './PopularSuperbet.vue';
import { getSuperbetsGroupId } from '@modules/eventGrid/components/EventRow/marketGroupHelpers';
import { MarketGroupPhase } from '@core/services/offer/_private/helpers/types';
import { SelectionSourceScreen } from '@superbet-group/web.types';
import {
    PopularSuperbet as PopularSuperbetType,
    PopularSuperbetV2 as PopularSuperbetV2Type,
} from '@store/modules/data/sportOffer/types';
import OfferEvent, { createMarket } from '@core/models/offer/OfferEvent';
import { getShellConfig } from '@src/config/shell/config';
import {
    createSelfCleaningClientStorage,
    SelfCleaningClientStorage,
} from '@core/utils/selfCleaningClientStorage/createSelfCleaningClientStorage';
import { PeerUrgencyExperimentVariant } from '@core/models/user/flags';
import { useSdsCarouselBreakpointVariant } from '@shared/composables/useSdsCarouselBreakpointVariant';

function getTomorrow() {
    const date = new Date();

    date.setDate(date.getDate() + 1);
    // The offset is set to 8h due to the bookie time included
    // as written in the [technical specification](https://www.notion.so/superbet/Technical-documentation-fcadb9c76d4b4e619834d0566897e0ce?pvs=4#cb47b54a08b34318a52f6e38cd4eda17)
    date.setHours(8, 0, 0, 0);

    return date;
}

export default defineComponent({
    props: {
        limit: {
            type: Number,
            required: false,
            default: 10,
        },
        sourceScreen: {
            type: Number as PropType<SelectionSourceScreen>,
            required: true,
        },
        sportId: {
            type: Number,
            required: false,
            default: undefined,
        },
        bettorNumberCache: {
            type: Object as PropType<SelfCleaningClientStorage<number, number>>,
            required: false,
            default: () => createSelfCleaningClientStorage<number, number>('bettorNumberCache'),
        },
        socialProofDesignVariant: {
            type: String as PropType<PeerUrgencyExperimentVariant>,
            required: false,
            default: PeerUrgencyExperimentVariant.None,
        },
        titleTranslationKey: {
            type: String,
            required: false,
            default: 'superOffer.popularSuperbets',
        },
        titleDescription: {
            type: String,
            required: false,
            default: undefined,
        },
    },
    setup(props) {
        const sportOfferDataStore = useVuexStore<SportOfferDataStore>('data/sportOffer');
        const isStructFetched = sportOfferDataStore.getter('isStructFetched');
        const popularSuperbets = sportOfferDataStore.getter('popularSuperbets');
        const popularSuperbetsV2 = sportOfferDataStore.getter('popularSuperbetsV2');
        const eventsMap = sportOfferDataStore.getter('eventsMap');
        const fetchMarketGroups = sportOfferDataStore.action('fetchMarketGroups');
        const fetchPopularSuperbets = sportOfferDataStore.action('fetchPopularSuperbets');
        const fetchPopularSuperbetsV2 = sportOfferDataStore.action('fetchPopularSuperbetsV2');
        watch(() => props.sportId, (sportId, oldSportId) => {
            if (!sportId || sportId === oldSportId) {
                return;
            }

            fetchMarketGroups({
                sportId,
                marketGroupPhase: MarketGroupPhase.Prematch,
            });
        }, {
            immediate: true,
        });

        const popularSuperbetsV2FF = getShellConfig().remoteConfig.popularSuperbetsV2;

        watch(() => isStructFetched.value, (val) => {
            if (val) {
                if (popularSuperbetsV2FF) {
                    fetchPopularSuperbetsV2();
                } else {
                    fetchPopularSuperbets();
                }
            }
        }, { immediate: true });

        // eslint-disable-next-line sonarjs/cognitive-complexity
        const popularSuperbetSlides = computed(() => {
            const fullPopularSuperbets = (popularSuperbetsV2FF ? popularSuperbetsV2 : popularSuperbets).value.map((popularSuperbet, index) => {
                // TODO: consider sending senttry warnings if any of the early returns is triggered as it indicated BE is returning invalid data

                if (popularSuperbetsV2FF) {
                    const popularSuperbetV2 = popularSuperbet as PopularSuperbetV2Type;
                    /**
                     * Event received from popular superbets endpoint will not be updated with SSE, so it can have stale data.
                     * If that event exists in store, we should use the one from store as it will have the latest data received over SSE.
                    */
                    const event = eventsMap.value[popularSuperbetV2.event.id]
                        ? eventsMap.value[popularSuperbetV2.event.id]
                        : popularSuperbetV2.event;

                    if (event.sportId !== props.sportId) {
                        return null;
                    }

                    if (!event.hasPreMatchBetting()) {
                        return null;
                    }

                    if (isAfterDate(event.date, getTomorrow())) {
                        return null;
                    }

                    let odd;

                    /**
                     * Event from store might not have the superbet odd, since it can have only preselected markets if match details are not fetched
                     * In this case we need to get the odd from response received from popular superbets endpoint
                    */
                    odd = event.getOddByUuid(popularSuperbetV2.popularSuperbetId)
                        ?? popularSuperbetV2.event.getOddByUuid(popularSuperbetV2.popularSuperbetId);

                    if (!odd) {
                        return null;
                    }

                    return {
                        event,
                        odd,
                        index,
                        markets: [createMarket(odd)],
                        id: odd.uuid,
                    };
                } else {
                    const popularSuperbetV1 = popularSuperbet as PopularSuperbetType;
                    const event = eventsMap.value[popularSuperbetV1.eventID] as OfferEvent;
                    if (!event) {
                        return null;
                    }

                    if (event.sportId !== props.sportId) {
                        return null;
                    }

                    if (!event.hasPreMatchBetting()) {
                        return null;
                    }

                    if (isAfterDate(event.date, getTomorrow())) {
                        return null;
                    }

                    const odd = event.getOddByUuid(popularSuperbetV1.oddID);

                    if (!odd) {
                        return null;
                    }

                    return {
                        id: odd.uuid,
                        event,
                        odd,
                        index,
                        markets: [createMarket(odd)],
                    };
                }
            }).filter((popularSuperbet) => popularSuperbet !== null).slice(0, props.limit);

            return fullPopularSuperbets.length < 2 ? [] : fullPopularSuperbets;
        });
        const getMarketGroupInfosForSportId = sportOfferDataStore.getter('getMarketGroupInfosForSportId');
        const superbetsMarketGroupId = computed(() =>
            getSuperbetsGroupId(
                getMarketGroupInfosForSportId.value(props.sportId),
                popularSuperbetSlides.value[0]?.markets ?? [],
            ));

        const { carouselLayoutVariant, carouselLayoutValue } = useSdsCarouselBreakpointVariant();

        return {
            carouselLayoutVariant,
            carouselLayoutValue,
            popularSuperbetSlides,
            superbetsMarketGroupId,
        };
    },
    components: {
        PopularSuperbet,
        SdsCarousel,
        SdsSectionTitle,
    },
});
